// import {
//   Cartesian2,
//   Cartesian3,
//   Cartographic,
//   defaultValue,
//   defined,
//   DeveloperError,
//   Ellipsoid,
//   Math,
//   Rectangle,
// } from "@cesium/engine";
import Cartesian2 from "../../Core/Cartesian2.js";
import Cartesian3 from "../../Core/Cartesian3.js";
import Cartographic from "../../Core/Cartographic.js";
import defaultValue from "../../Core/defaultValue.js";
import defined from "../../Core/defined.js";
import DeveloperError from "../../Core/DeveloperError.js";
import Ellipsoid from "../../Core/Ellipsoid.js";
import Math from "../../Core/Math.js";
import Rectangle from "../../Core/Rectangle.js";

import proj4 from "proj4";

/**
 * EPSG:5179 정보 입력
 */
proj4.defs(
  "EPSG:5179",
  "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +units=m +no_defs"
);

const cache = {};
const cache1 = {};

const Proj4WebMapProjection = (function () {
  /**
   * Proj4WebMapProjection
   * @param {Ellipsoid} ellipsoid Cesium Ellipsoid
   * @param {string} code EPSG code
   */
  function Proj4WebMapProjection(ellipsoid, code) {
    if (ellipsoid === void 0) {
      ellipsoid = Ellipsoid.WGS84;
    } // WGS84 Ellipsoid를 기본값으로 사용
    if (code === void 0) {
      code = "EPSG:5179";
    } // EPSG:5179 좌표계를 기본값으로 사용
    this.ellipsoid = ellipsoid;
    this._proj = proj4(code);
  }

  /**
   * Convert longitude and latitude values to set coordinate system values
   * @param {Cartographic} cartographic
   * @param {Cartesian3} result
   * @returns {Cartesian3}
   */
  Proj4WebMapProjection.prototype.project = function (cartographic, result) {
    // 경위도 => EPSG:XXXX 좌표
    const epgscustom = this._proj.forward([
      Math.toDegrees(cartographic.longitude),
      Math.toDegrees(cartographic.latitude),
    ]);
    if (!defined(result)) {
      return new Cartesian3(epgscustom[0], epgscustom[1], cartographic.height);
    }
    result.x = epgscustom[0];
    result.y = epgscustom[1];
    result.z = cartographic.height;
    return result;
  };

  /**
   * Convert coordinate values of the set coordinate system to longitude and latitude
   * @param {Cartesian3} cartesian
   * @param {Cartographic} result
   * @returns {Cartographic}
   */
  Proj4WebMapProjection.prototype.unproject = function (cartesian, result) {
    // EPSG:XXXX 좌표 => 경위도
    const wgs84 = this._proj.inverse([cartesian.x, cartesian.y, cartesian.z]);
    if (!defined(result)) {
      return new Cartographic(
        Math.toRadians(wgs84[0]),
        Math.toRadians(wgs84[1]),
        cartesian.z
      );
    }
    result.longitude = Math.toRadians(wgs84[0]);
    result.latitude = Math.toRadians(wgs84[1]);
    result.height = cartesian.z;
    return result;
  };

  return Proj4WebMapProjection;
})();

// /**
//  * EPSG:5179 coordinate system tiling schema
//  * @alias Epsg5179TilingSchema
//  */
// const Epsg5179TilingSchema = (function () {
/*
 * EPSG:5179 tiling schema
 * @alias Epsg5179TilingSchema
 * @param {Object} options
 * @param {Ellipsoid} options.ellipsoid
 * @param {number} options.rectangleSouthwestInMeters
 * @param {number} options.rectangleNortheastInMeters
 */
function Epsg5179TilingSchema(options) {
  // constructor
  if (options === void 0) {
    options = {};
  }
  this._numberOfLevelZeroTilesX = 1; // 0 레벨 X축 타일 개수
  this._numberOfLevelZeroTilesY = 1; // 0 레벨 Y축 방향 타일 개수
  this._rectangleSouthwestInMeters = new Cartesian2(); // 0 레벨 X축 전체 타일 범위
  this._rectangleNortheastInMeters = new Cartesian2(); // 0 레벨 Y축 전체 타일 범위
  this.skipfirst2 = 2; //Have to skip those two to ignore the guard in ImageProvider.

  // 입력받은 파라미터 값 적용
  this.ellipsoid = defaultValue(options.ellipsoid, Ellipsoid.WGS84);
  this._numberOfLevelZeroTilesX = defaultValue(
    options.numberOfLevelZeroTilesX,
    1
  );
  this._numberOfLevelZeroTilesY = defaultValue(
    options.numberOfLevelZeroTilesY,
    1
  );
  this.projection = new Proj4WebMapProjection(this.ellipsoid);
  if (
    defined(options.rectangleSouthwestInMeters) &&
    defined(options.rectangleNortheastInMeters)
  ) {
    this._rectangleSouthwestInMeters = options.rectangleSouthwestInMeters;
    this._rectangleNortheastInMeters = options.rectangleNortheastInMeters;
  } else {
    throw new DeveloperError(
      "The rectangle should be given for the extend, and it should be fitted to the tile extent"
    );
  }

  // 전체 타일 범위 설정
  const southwest = this.projection.unproject(this._rectangleSouthwestInMeters);
  const northeast = this.projection.unproject(this._rectangleNortheastInMeters);
  this.rectangle = new Rectangle(
    southwest.longitude,
    southwest.latitude,
    northeast.longitude,
    northeast.latitude
  );
}

/**
 * The number of X-axis tiles in the input level
 * @param {number} level
 * @returns {number}
 */
Epsg5179TilingSchema.prototype.getNumberOfXTilesAtLevel = function (level) {
  return this._numberOfLevelZeroTilesX << level;
};

/**
 * The number of Y-axis tiles in the input level
 * @param {number} level
 * @returns {number}
 */
Epsg5179TilingSchema.prototype.getNumberOfYTilesAtLevel = function (level) {
  return this._numberOfLevelZeroTilesY << level;
};

/**
 * Convert Rectangle to set coordinate system format
 * @param {Rectangle} rectangle
 * @param {Rectangle} result
 * @returns {Rectangle}
 */
Epsg5179TilingSchema.prototype.rectangleToNativeRectangle = function (
  rectangle,
  result
) {
  const southwest = this.projection.project(Rectangle.southwest(rectangle));
  const northeast = this.projection.project(Rectangle.northeast(rectangle));
  if (!defined(result)) {
    return new Rectangle(southwest.x, southwest.y, northeast.x, northeast.y);
  }
  result.west = southwest.x;
  result.south = southwest.y;
  result.east = northeast.x;
  result.north = northeast.y;
  return result;
};

/**
 * Convert the tile range corresponding to X, Y, LEVEL to the set coordinate system format
 * @param {number} x
 * @param {number} y
 * @param {number} level
 * @param {Rectangle} result
 * @returns {Rectangle}
 */
Epsg5179TilingSchema.prototype.tileXYToNativeRectangle = function (
  x,
  y,
  level,
  result
) {
  const xTiles = this.getNumberOfXTilesAtLevel(level);
  const yTiles = this.getNumberOfYTilesAtLevel(level);
  const xTileWidth =
    (this._rectangleNortheastInMeters.x - this._rectangleSouthwestInMeters.x) /
    xTiles;
  const west = this._rectangleSouthwestInMeters.x + x * xTileWidth;
  const east = this._rectangleSouthwestInMeters.x + (x + 1) * xTileWidth;
  const yTileHeight =
    (this._rectangleNortheastInMeters.y - this._rectangleSouthwestInMeters.y) /
    yTiles;
  const north = this._rectangleNortheastInMeters.y - y * yTileHeight;
  const south = this._rectangleNortheastInMeters.y - (y + 1) * yTileHeight;

  cache1[x.toString() + y.toString() + level.toString()] = {
    tw: xTileWidth,
    th: yTileHeight,
    west: west,
    east: east,
    north: north,
    south: south,
  };

  if (!defined(result)) {
    return new Rectangle(west, south, east, north);
  }

  result.west = west;
  result.south = south;
  result.east = east;
  result.north = north;

  return result;
};

/**
 * Convert tile range corresponding to X, Y, LEVEL to longitude and latitude
 * @param {number} x
 * @param {number} y
 * @param {number} level
 * @param {Rectangle} result
 * @returns {Rectangle}
 */
Epsg5179TilingSchema.prototype.tileXYToRectangle = function (
  x,
  y,
  level,
  result
) {
  const key = x.toString() + y.toString() + level.toString();

  if (cache[key]) {
    return cache[key];
  }

  const nativeRectangle = this.tileXYToNativeRectangle(x, y, level, result);
  const southwest = this.projection.unproject(
    new Cartesian3(nativeRectangle.west, nativeRectangle.south, 0)
  );
  const northeast = this.projection.unproject(
    new Cartesian3(nativeRectangle.east, nativeRectangle.north, 0)
  );

  nativeRectangle.west = southwest.longitude;
  nativeRectangle.south = southwest.latitude;
  nativeRectangle.east = northeast.longitude;
  nativeRectangle.north = northeast.latitude;

  cache[key] = nativeRectangle;

  return nativeRectangle;
};

/**
 * Returns the X, Y values of the tile that includes the input coordinate values
 * @param {Cartographic} position
 * @param {number} level
 * @param {Cartesian2|undefined} result
 * @returns {Cartesian2|undefined}
 */
Epsg5179TilingSchema.prototype.positionToTileXY = function (
  position,
  level,
  result
) {
  if (this.skipfirst2 && this.skipfirst2-- > 0) {
    return new Cartesian2(0, 0);
  }
  // 영역 범위 밖의 타일
  if (!Rectangle.contains(this.rectangle, position)) {
    return undefined;
  }

  const xTiles = this.getNumberOfXTilesAtLevel(level); // 입력 레벨의 X축 타일 개수
  const yTiles = this.getNumberOfYTilesAtLevel(level); // 입력 레벨의 Y축 타일 개수
  const overallWidth =
    this._rectangleNortheastInMeters.x - this._rectangleSouthwestInMeters.x; // X축 좌표 범위
  const xTileWidth = overallWidth / xTiles; // 타일 X축 길이
  const overallHeight =
    this._rectangleNortheastInMeters.y - this._rectangleSouthwestInMeters.y; // Y축 좌표 범위
  const yTileHeight = overallHeight / yTiles; // 타일 Y축 길이
  const webMercatorPosition = this.projection.project(position); // EPSG:XXXX 좌표 변환
  const distanceFromWest =
    webMercatorPosition.x - this._rectangleSouthwestInMeters.x;
  const distanceFromNorth =
    this._rectangleNortheastInMeters.y - webMercatorPosition.y;
  let xTileCoordinate = (distanceFromWest / xTileWidth) | 0;
  if (xTileCoordinate >= xTiles) {
    xTileCoordinate = xTiles - 1;
  }
  let yTileCoordinate = (distanceFromNorth / yTileHeight) | 0;
  if (yTileCoordinate >= yTiles) {
    yTileCoordinate = yTiles - 1;
  }
  if (!defined(result)) {
    return new Cartesian2(xTileCoordinate, yTileCoordinate);
  }
  result.x = xTileCoordinate;
  result.y = yTileCoordinate;
  return result;
};

//   return Epsg5179TilingSchema;
// })();

// /**
//  * Ditap EPSG:5179 coordinate system tiling schema
//  * @alias DitapEpsg5179TilingSchema
//  */
class DitapEpsg5179TilingSchema extends Epsg5179TilingSchema {
  constructor() {
    // return this.getEpsg5179TilingSchema();
    // 타일 전체 영역(단위는 해당 좌표계 기준)
    const MAP_BOUNDS_SOUTH = -200000;
    const MAP_BOUNDS_EASR = 4000000;
    const MAP_BOUNDS_WEST = 791357.44;
    const MAP_BOUNDS_NORTH = 2473868.8;

    const MAP_ZERO_TILE_X = 5; // 0레벨 X축 타일 갯수
    const MAP_ZERO_TILE_Y = 6; // 0레벨 Y축 타일 갯수

    const options = {
      rectangleSouthwestInMeters: new Cartesian2(
        MAP_BOUNDS_SOUTH,
        MAP_BOUNDS_WEST
      ),
      rectangleNortheastInMeters: new Cartesian2(
        MAP_BOUNDS_NORTH,
        MAP_BOUNDS_EASR
      ),
      numberOfLevelZeroTilesX: MAP_ZERO_TILE_X,
      numberOfLevelZeroTilesY: MAP_ZERO_TILE_Y,
    };
    super(options);
  }
  // /**
  //  * Return EPSG:5179 coordinate system tiling schema
  //  * @returns {Epsg5179TilingSchema}
  //  */
  // getEpsg5179TilingSchema() {
  //   // 타일 전체 영역(단위는 해당 좌표계 기준)
  //   const MAP_BOUNDS_SOUTH = -200000;
  //   const MAP_BOUNDS_EASR = 4000000;
  //   const MAP_BOUNDS_WEST = 791357.44;
  //   const MAP_BOUNDS_NORTH = 2473868.8;

  //   const MAP_ZERO_TILE_X = 5; // 0레벨 X축 타일 갯수
  //   const MAP_ZERO_TILE_Y = 6; // 0레벨 Y축 타일 갯수

  //   const options = {
  //     rectangleSouthwestInMeters: new Cartesian2(
  //       MAP_BOUNDS_SOUTH,
  //       MAP_BOUNDS_WEST
  //     ),
  //     rectangleNortheastInMeters: new Cartesian2(
  //       MAP_BOUNDS_NORTH,
  //       MAP_BOUNDS_EASR
  //     ),
  //     numberOfLevelZeroTilesX: MAP_ZERO_TILE_X,
  //     numberOfLevelZeroTilesY: MAP_ZERO_TILE_Y,
  //   };

  //   return new Epsg5179TilingSchema(options);
  // }
}
export default DitapEpsg5179TilingSchema;
